import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api, { API } from "@/lib/api";
import { X as XIcon, ChevronLeft, ChevronRight } from "lucide-react";
import FadeIn from "@/components/FadeIn";

const DEFAULTS = [
  "https://images.unsplash.com/photo-1551183053-bf91a1d81141?auto=format&fit=crop&w=1400&q=80",
  "https://images.unsplash.com/photo-1473093295043-cdd812d0e601?auto=format&fit=crop&w=1400&q=80",
  "https://images.unsplash.com/photo-1572715376701-98568319fd0b?auto=format&fit=crop&w=1400&q=80",
  "https://images.unsplash.com/photo-1546833999-b9f581a1996d?auto=format&fit=crop&w=1400&q=80",
  "https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?auto=format&fit=crop&w=1400&q=80",
  "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1400&q=80",
  "https://images.unsplash.com/photo-1565958011703-44f9829ba187?auto=format&fit=crop&w=1400&q=80",
  "https://images.unsplash.com/photo-1571877227200-a0d98ea607e9?auto=format&fit=crop&w=1400&q=80",
];

const ASPECTS = [
  "aspect-[3/4]", "aspect-[4/3]", "aspect-square", "aspect-[3/4]",
  "aspect-[4/3]", "aspect-square", "aspect-[3/4]", "aspect-square",
];

const resolveImg = (u) => {
  if (!u) return null;
  if (u.startsWith("http")) return u;
  return `${API.replace(/\/api$/, "")}${u}`;
};

export default function Galleria() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [lightbox, setLightbox] = useState(-1);

  useEffect(() => {
    api.get("/gallery").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  const photos = items.length > 0 ? items.map((g) => resolveImg(g.image_url)) : DEFAULTS;

  const close = useCallback(() => setLightbox(-1), []);
  const prev = useCallback((e) => {
    e?.stopPropagation();
    setLightbox((i) => (i <= 0 ? photos.length - 1 : i - 1));
  }, [photos.length]);
  const next = useCallback((e) => {
    e?.stopPropagation();
    setLightbox((i) => (i + 1) % photos.length);
  }, [photos.length]);

  useEffect(() => {
    if (lightbox < 0) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox, close, prev, next]);

  return (
    <div data-testid="galleria-page" className="bg-[#FAF9F6]">
      <section className="pt-20 sm:pt-28 pb-12 sm:pb-16">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <FadeIn className="max-w-3xl">
            <p className="text-xs tracking-[0.3em] uppercase text-[#5C5C59] font-semibold">
              {t("galleria.eyebrow")}
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-none mt-5">
              {t("galleria.title")}
            </h1>
            <p className="text-[#5C5C59] mt-6 leading-relaxed">{t("galleria.subtitle")}</p>
          </FadeIn>
        </div>
      </section>

      <section className="pb-24 sm:pb-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          {photos.length === 0 ? (
            <p className="text-[#5C5C59] text-center py-20">{t("galleria.empty")}</p>
          ) : (
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 sm:gap-5 [column-fill:_balance]">
              {photos.map((src, i) => (
                <FadeIn
                  key={i}
                  delay={Math.min(i, 6) * 60}
                  as="button"
                  onClick={() => setLightbox(i)}
                  data-testid={`galleria-photo-${i}`}
                  className={`group relative w-full mb-4 sm:mb-5 overflow-hidden bg-[#E5E3D8] ${ASPECTS[i % ASPECTS.length]} break-inside-avoid block`}
                >
                  <img
                    src={src}
                    alt={`Foto ${i + 1}`}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                </FadeIn>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightbox >= 0 && (
        <div
          data-testid="galleria-lightbox"
          onClick={close}
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 sm:p-12 animate-in fade-in"
          style={{ animation: "fadeIn 220ms ease-out" }}
        >
          <button
            onClick={(e) => { e.stopPropagation(); close(); }}
            data-testid="lightbox-close"
            aria-label="Chiudi"
            className="absolute top-4 right-4 sm:top-6 sm:right-8 text-white/80 hover:text-white p-2"
          >
            <XIcon className="w-8 h-8" strokeWidth={1.5} />
          </button>
          <button
            onClick={prev}
            data-testid="lightbox-prev"
            aria-label="Precedente"
            className="absolute left-2 sm:left-8 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2"
          >
            <ChevronLeft className="w-9 h-9" strokeWidth={1.5} />
          </button>
          <button
            onClick={next}
            data-testid="lightbox-next"
            aria-label="Successiva"
            className="absolute right-2 sm:right-8 top-1/2 -translate-y-1/2 text-white/80 hover:text-white p-2"
          >
            <ChevronRight className="w-9 h-9" strokeWidth={1.5} />
          </button>
          <img
            src={photos[lightbox]}
            alt={`Foto ${lightbox + 1}`}
            onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] max-w-[92vw] object-contain shadow-2xl"
          />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-xs tracking-wider">
            {lightbox + 1} / {photos.length}
          </div>
        </div>
      )}
    </div>
  );
}
